import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Navbar } from '../components/Navbar'
import { PropertyCard } from '../components/PropertyCard'
import { AuthModal } from '../components/AuthModal'
import { Button } from '../components/ui/button'
import { propertiesAPI } from '../lib/api'
import { useAuth } from '../context/AuthContext'
import { useCurrency } from '../context/CurrencyContext'
import { useTenant } from '../context/TenantContext'
import { 
  Building2, 
  Shield, 
  TrendingUp, 
  Users, 
  ArrowRight, 
  Wallet,
  CheckCircle2,
  Globe,
  Lock,
  Coins,
  ChevronDown
} from 'lucide-react'

export function LandingPage() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { isAuthenticated } = useAuth()
  const { formatPrice } = useCurrency()
  const { tenant } = useTenant()
  const [authOpen, setAuthOpen] = useState(false)
  const [featuredProperties, setFeaturedProperties] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const loadProperties = async () => {
      try {
        const res = await propertiesAPI.list({ limit: 3 })
        setFeaturedProperties(Array.isArray(res.data) ? res.data : [])
      } catch (error) {
        console.error('Failed to load properties:', error)
      } finally {
        setLoading(false)
      }
    }
    loadProperties()
  }, [])

  const stats = [
    { label: t('stats.tvl'), value: formatPrice(12500000, { compact: true }), icon: Coins },
    { label: t('stats.properties'), value: '24', icon: Building2 },
    { label: t('stats.investors'), value: '1,200+', icon: Users },
    { label: t('stats.yield'), value: '8.5%', icon: TrendingUp },
  ]

  const features = [
    { icon: Shield, title: t('features.compliant.title'), description: t('features.compliant.description') },
    { icon: Globe, title: t('features.global.title'), description: t('features.global.description') },
    { icon: Lock, title: t('features.secure.title'), description: t('features.secure.description') },
    { icon: Coins, title: t('features.fractional.title'), description: t('features.fractional.description') },
  ]

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center bg-brand-secondary overflow-hidden" data-testid="hero-section">
        {/* Background Image with Ken Burns */}
        <div className="absolute inset-0 hero-bg-kenburns">
          <img
            src="https://images.unsplash.com/photo-1772090125408-5640e66f46e7?w=1920&q=80&auto=format"
            alt=""
            className="w-full h-full object-cover"
            loading="eager"
          />
        </div>

        {/* Dark gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-brand-secondary/90 via-brand-secondary/75 to-brand-secondary/95" />

        {/* Brand-colored radial glow */}
        <div className="absolute inset-0 hero-radial-glow" />

        {/* Animated grid lines */}
        <div className="absolute inset-0 opacity-[0.04] hero-grid-pulse">
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff_1px,transparent_1px),linear-gradient(to_bottom,#ffffff_1px,transparent_1px)] bg-[size:60px_60px]" />
        </div>

        {/* Light sweep animation */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="hero-light-sweep" />
        </div>

        {/* Floating particles */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="hero-particle hero-particle-1" />
          <div className="hero-particle hero-particle-2" />
          <div className="hero-particle hero-particle-3" />
          <div className="hero-particle hero-particle-4" />
          <div className="hero-particle hero-particle-5" />
          <div className="hero-particle hero-particle-6" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-20 z-10">
          <div className="text-center max-w-4xl mx-auto">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
              <span className="inline-block px-4 py-2 border border-brand-primary/50 bg-brand-primary/10 text-brand-primary text-xs font-semibold uppercase tracking-widest mb-8 backdrop-blur-sm">
                {t('hero.badge')}
              </span>
            </motion.div>

            <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
              className="text-4xl md:text-5xl lg:text-6xl font-heading text-white leading-tight drop-shadow-[0_2px_20px_rgba(0,0,0,0.5)]">
              {t('hero.title1')}{' '}
              <span className="text-brand-primary hero-text-glow">{t('hero.title2')}</span>
              <br />{t('hero.title3')}
            </motion.h1>

            <motion.div initial={{ opacity: 0, scaleX: 0 }} animate={{ opacity: 1, scaleX: 1 }} transition={{ delay: 0.3 }}
              className="w-16 h-px bg-brand-primary mx-auto my-8 shadow-[0_0_12px_rgba(var(--brand-primary-rgb)/0.6)]" />

            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
              className="text-lg text-white/70 max-w-2xl mx-auto font-body drop-shadow-md">
              {t('hero.subtitle')}
            </motion.p>

            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}
              className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button onClick={() => isAuthenticated ? navigate('/marketplace') : setAuthOpen(true)}
                className="h-16 px-10 bg-brand-primary hover:bg-brand-primary-hover text-brand-secondary font-semibold tracking-wide text-base rounded-none shadow-[0_0_30px_rgba(var(--brand-primary-rgb)/0.3)] hover:shadow-[0_0_40px_rgba(var(--brand-primary-rgb)/0.5)] transition-shadow duration-300" data-testid="hero-cta-btn">
                <Wallet className="w-5 h-5 mr-2" />{t('hero.cta')}<ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button onClick={() => navigate('/marketplace')} variant="outline"
                className="h-16 px-10 bg-transparent border-white/20 text-white hover:bg-white/10 hover:border-white/30 text-base rounded-none backdrop-blur-sm" data-testid="explore-btn">
                {t('hero.explore')}
              </Button>
            </motion.div>

            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }}
              className="mt-16 flex flex-wrap items-center justify-center gap-8 text-white/50">
              <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-brand-primary" /><span className="text-sm font-semibold tracking-wide">{t('trust.secCompliant')}</span></div>
              <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-brand-primary" /><span className="text-sm font-semibold tracking-wide">{t('trust.kycVerified')}</span></div>
              <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-brand-primary" /><span className="text-sm font-semibold tracking-wide">{t('trust.audited')}</span></div>
            </motion.div>
          </div>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1 }} className="absolute bottom-10 left-1/2 -translate-x-1/2">
            <ChevronDown className="w-6 h-6 text-brand-primary animate-bounce" />
          </motion.div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-20 bg-[#F8FAFC] border-y border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div key={index} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.1 }} viewport={{ once: true }} className="text-center">
                <stat.icon className="w-8 h-8 mx-auto mb-3 text-brand-primary" />
                <p className="text-3xl font-heading text-brand-secondary font-mono">{stat.value}</p>
                <p className="text-sm text-slate-500 mt-1 uppercase tracking-wider">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Properties */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 border border-brand-primary/50 bg-brand-primary/10 text-brand-primary text-xs font-semibold uppercase tracking-widest mb-4">{t('featured.badge')}</span>
            <h2 className="text-3xl md:text-4xl font-heading text-brand-secondary">{t('featured.title')}</h2>
            <div className="w-16 h-px bg-brand-primary mx-auto mt-6" />
            <p className="text-slate-500 mt-4 max-w-xl mx-auto">{t('featured.subtitle')}</p>
          </div>
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3].map((i) => (<div key={i} className="h-[450px] bg-[#F8FAFC] animate-pulse" />))}
            </div>
          ) : featuredProperties.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {featuredProperties.map((property, index) => (<PropertyCard key={property.id} property={property} index={index} />))}
            </div>
          ) : (
            <div className="text-center py-16 bg-[#F8FAFC] border border-slate-100">
              <Building2 className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">{t('featured.noProperties')}</p>
              <p className="text-sm text-slate-400 mt-1">{t('featured.checkBack')}</p>
            </div>
          )}
          <div className="text-center mt-12">
            <Button onClick={() => navigate('/marketplace')} variant="outline"
              className="h-14 px-8 border-brand-secondary text-brand-secondary hover:bg-brand-secondary hover:text-white font-semibold tracking-wide rounded-none" data-testid="view-all-btn">
              {t('featured.viewAll')}<ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-24 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 border border-brand-primary/50 bg-brand-primary/10 text-brand-primary text-xs font-semibold uppercase tracking-widest mb-4">{t('features.badge')}</span>
            <h2 className="text-3xl md:text-4xl font-heading text-brand-secondary">{t('features.title')}</h2>
            <div className="w-16 h-px bg-brand-primary mx-auto mt-6" />
            <p className="text-slate-500 mt-4 max-w-xl mx-auto">{t('features.subtitle')}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <motion.div key={index} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.1 }} viewport={{ once: true }}
                className="p-6 bg-white border border-slate-100 hover:border-brand-primary/30 transition-all duration-300 group">
                <div className="w-14 h-14 bg-brand-primary/10 flex items-center justify-center mb-4 group-hover:bg-brand-primary/20 transition-colors">
                  <feature.icon className="w-7 h-7 text-brand-primary" />
                </div>
                <h3 className="text-lg font-heading text-brand-secondary mb-2">{feature.title}</h3>
                <p className="text-sm text-slate-500">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-brand-secondary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-block px-4 py-2 border border-brand-primary/50 bg-brand-primary/10 text-brand-primary text-xs font-semibold uppercase tracking-widest mb-4">{t('cta.badge')}</span>
          <h2 className="text-3xl md:text-4xl font-heading text-white mb-4">{t('cta.title')}</h2>
          <div className="w-16 h-px bg-brand-primary mx-auto my-6" />
          <p className="text-white/60 max-w-xl mx-auto mb-10">{t('cta.subtitle')}</p>
          <Button onClick={() => isAuthenticated ? navigate('/marketplace') : setAuthOpen(true)}
            className="h-16 px-10 bg-brand-primary hover:bg-brand-primary-hover text-brand-secondary font-semibold tracking-wide rounded-none" data-testid="cta-btn">
            {t('cta.button')}<ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-brand-secondary border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <img src={tenant?.branding?.logo_url || "https://uni2ecosystem.com/images/logos/tokenizelogo.png"} alt={tenant?.name || "2kenize"} className="h-8 w-auto" />
            <p className="text-sm text-white/50">{t('footer.copyright')}</p>
          </div>
        </div>
      </footer>

      <AuthModal open={authOpen} onOpenChange={setAuthOpen} />
    </div>
  )
}
